// components/ProductDetailView.jsx
import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Stack,
  IconButton,
  Rating,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WishlistToggle from "./WishlistToggle";
import VirtualTryOn from "./VirtualTryOn";
import { useCart } from "../context/CartContext";

// Styled Components (same as before)
const ThumbnailImage = styled(Box)(({ theme, selected }) => ({
  width: 60,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  cursor: "pointer",
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "3px solid transparent",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    border: `3px solid ${theme.palette.grey[900]}`,
    transform: "scale(1.05)",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const MainImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: "hidden",
  backgroundColor: "#f5f5f5",
  height: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.95,
  },
}));

const MainImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  pointerEvents: "none",
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  zIndex: 2,
}));

const SizeButton = styled(Button)(({ theme, selected }) => ({
  minWidth: 80,
  padding: "12px 20px",
  border: selected
    ? `2px solid ${theme.palette.grey[900]}`
    : `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: selected ? theme.palette.grey[900] : "white",
  color: selected ? "white" : theme.palette.grey[800],
  borderRadius: 4,
  fontSize: "0.875rem",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: selected ? theme.palette.grey[800] : theme.palette.grey[50],
    border: `2px solid ${theme.palette.grey[900]}`,
  },
}));

const ColorCircle = styled(Box)(({ theme, selected, colorvalue }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: colorvalue || theme.palette.grey[300],
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "2px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  position: "relative",
  "&:hover": {
    transform: "scale(1.1)",
    border: `3px solid ${theme.palette.grey[900]}`,
  },
  "&::after": selected
    ? {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "white",
        border: "1px solid rgba(0,0,0,0.3)",
      }
    : {},
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const QuantitySelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 0,
  padding: "8px 12px",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

// Utility: color mapping
const getColorHex = (colorName) => {
  const colorMap = {
    black: "#000000",
    white: "#FFFFFF",
    "mesh black": "#1a1a1a",
    "off white": "#F5F5DC",
    brown: "#8B4513",
    beige: "#F5F5DC",
    blue: "#0000FF",
    red: "#FF0000",
    green: "#008000",
    yellow: "#FFFF00",
    pink: "#FFC0CB",
    gray: "#808080",
    grey: "#808080",
  };
  return colorMap[colorName?.toLowerCase()] || "#808080";
};

// Utility: clean image URL
const cleanImageUrl = (url) => {
  if (typeof url !== "string") return "";
  return url.trim().replace(/storage\.cloud\.google\.com/g, "storage.googleapis.com");
};

// Utility: safe seed for Picsum (NO SPACES!)
const safeSeed = (input) => {
  return encodeURIComponent(String(input).replace(/\s+/g, "-").substring(0, 50));
};

// ✅ MAIN COMPONENT — now takes product data as prop
export default function ProductDetailView({ 
  productDetail, 
  customerId,
  onBack 
}) {
  const { cartItems, addToCart, updateQuantity } = useCart();

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewQuantity, setPreviewQuantity] = useState(1);

  const [completeTheLook, setCompleteTheLook] = useState(null);
  const [loadingCompleteTheLook, setLoadingCompleteTheLook] = useState(false);
  const [tryOnOpen, setTryOnOpen] = useState(false);

  // Extract product safely
  const product = productDetail?.product;

  // Collect all images
  const allImages = useMemo(() => {
    const images = [];
    if (product?.images) {
      product.images.forEach((img, i) =>
        images.push({ url: img, label: `Main ${i + 1}` })
      );
    }
    if (productDetail?.variants) {
      productDetail.variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) =>
            images.push({ url: img, label: variant.attributes?.color || "Variant" })
          );
        }
      });
    }
    return images.length > 0 ? images : [{ url: "", label: "No Image" }];
  }, [productDetail]);

  // Colors
  const colorList = useMemo(() => {
    const colors = [];
    if (productDetail?.variants) {
      productDetail.variants.forEach((v) => {
        if (v.attributes?.color) colors.push(v.attributes.color);
      });
    }
    if (colors.length === 0 && product?.attributes?.color) {
      const productColors = String(product.attributes.color).split(",").map(c => c.trim()).filter(Boolean);
      colors.push(...productColors);
    }
    return [...new Set(colors)]; // dedupe
  }, [productDetail]);

  // Sizes
  const sizeList = useMemo(() => {
    if (!product?.attributes?.size) return [];
    return Array.isArray(product.attributes.size)
      ? product.attributes.size
      : String(product.attributes.size).split(",").map(s => s.trim()).filter(Boolean);
  }, [product]);

  // Cart state
  const existingCartItem = useMemo(() => {
    if (!product?.id || !selectedSize) return null;
    return cartItems.find(
      (item) =>
        item.id === product.id &&
        item.size === selectedSize &&
        item.color === selectedColor
    );
  }, [cartItems, product?.id, selectedSize, selectedColor]);

  const isInCart = !!existingCartItem;
  const quantity = isInCart ? existingCartItem.quantity : previewQuantity;

  // Load Complete the Look (if needed)
  useEffect(() => {
    if (!customerId || !product?.id) return;
    const load = async () => {
      setLoadingCompleteTheLook(true);
      try {
        const data = await import("../api/productDetailApi").then(api =>
          api.fetchCompleteTheLook(customerId, product.id)
        );
        const formatted = await import("../api/productDetailApi").then(api =>
          api.formatCompleteTheLookData(data)
        );
        setCompleteTheLook(formatted);
      } catch (err) {
        console.error("Failed to load Complete the Look:", err);
      } finally {
        setLoadingCompleteTheLook(false);
      }
    };
    load();
  }, [customerId, product?.id]);

  // Set default size/color on mount
  useEffect(() => {
    if (colorList.length > 0 && !selectedColor) {
      setSelectedColor(colorList[0]);
    }
    if (sizeList.length > 0 && !selectedSize) {
      setSelectedSize(sizeList[0]);
    }
  }, [colorList, sizeList, selectedColor, selectedSize]);

  // Handlers
  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Please select a size before adding to cart.");
      return;
    }
    if (!product) return;

    if (existingCartItem) {
      if (quantity !== existingCartItem.quantity) {
        updateQuantity(existingCartItem.id, quantity);
      }
      return;
    }

    addToCart({
      id: product.id,
      title: product.title || "Product",
      price: parseFloat(product.price_info?.price || product.price_info?.originalPrice || 0),
      currency: product.price_info?.currencyCode || product.price_info?.currency || "USD",
      description: product.description || "",
      image: product.images?.[0] ? cleanImageUrl(product.images[0]) : "",
      size: selectedSize,
      color: selectedColor,
      variants: productDetail.variants || [],
      quantity: previewQuantity,
    });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const incrementQuantity = () => {
    if (isInCart) {
      updateQuantity(existingCartItem.id, existingCartItem.quantity + 1);
    } else {
      setPreviewQuantity((prev) => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (isInCart) {
      if (existingCartItem.quantity > 1) {
        updateQuantity(existingCartItem.id, existingCartItem.quantity - 1);
      } else {
        updateQuantity(existingCartItem.id, 0);
      }
    } else if (previewQuantity > 1) {
      setPreviewQuantity((prev) => prev - 1);
    }
  };

  const handleCompleteTheLookClick = (rec) => {
    if (rec?.id && onBack) {
      // Optional: notify parent to navigate
      onBack(`/item/${rec.id}`);
    }
  };

  // Derived data
  const originalPrice = parseFloat(product?.price_info?.originalPrice) || 0;
  const displayPrice = parseFloat(product?.price_info?.price) || originalPrice;
  const isOnSale = originalPrice > displayPrice && displayPrice > 0;
  const rating = parseFloat(product?.attributes?.rating) || 4.9;
  const reviewCount = parseInt(product?.attributes?.review_count) || 89;

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ChevronLeftIcon />} onClick={onBack} sx={{ mb: 3 }}>
          Back
        </Button>
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h5" color="error">Product data missing</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ChevronLeftIcon />}
        onClick={onBack}
        sx={{ mb: 3, textTransform: "none", fontWeight: 500 }}
      >
        Back
      </Button>

      <Grid container spacing={6} sx={{ mb: 8 }}>
        {/* Image Gallery */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box 
              sx={{ 
                width: 80, 
                overflowY: 'auto', 
                maxHeight: 'calc(100vh - 200px)',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': { 
                  backgroundColor: 'rgba(0,0,0,0.2)', 
                  borderRadius: '4px' 
                }
              }}
            >
              <Stack spacing={1.5}>
                {allImages.map((image, index) => (
                  <ThumbnailImage
                    key={index}
                    selected={currentImageIndex === index}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={cleanImageUrl(image.url) || `https://picsum.photos/seed/${safeSeed(index)}/100/120`}
                      alt={`Thumbnail ${index + 1}`}
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${safeSeed(index)}/100/120`;
                      }}
                    />
                  </ThumbnailImage>
                ))}
              </Stack>
            </Box>

            <Box sx={{ flex: 1 }}>
              <MainImageContainer onClick={() => setTryOnOpen(true)}>
                <MainImage
                  src={
                    allImages[currentImageIndex]?.url
                      ? cleanImageUrl(allImages[currentImageIndex].url)
                      : `https://picsum.photos/seed/${safeSeed(product.id)}/800/600`
                  }
                  alt={product.title}
                  onError={(e) => {
                    e.target.src = `https://picsum.photos/seed/${safeSeed(product.id)}/800/600`;
                  }}
                />

                {allImages.length > 1 && (
                  <>
                    <NavButton onClick={(e) => { e.stopPropagation(); handlePrevImage(); }} sx={{ left: 16 }}>
                      <ChevronLeftIcon />
                    </NavButton>
                    <NavButton onClick={(e) => { e.stopPropagation(); handleNextImage(); }} sx={{ right: 16 }}>
                      <ChevronRightIcon />
                    </NavButton>
                  </>
                )}

                <Box sx={{ position: "absolute", top: 16, left: 16 }} onClick={(e) => e.stopPropagation()}>
                  <Chip
                    icon={<Box component="span" sx={{ fontSize: "16px" }}>⭐</Box>}
                    label="Highly Rated"
                    size="small"
                    sx={{
                      bgcolor: "white",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      boxShadow: 1,
                      height: "28px",
                    }}
                  />
                </Box>
                <Box 
                  sx={{ position: "absolute", top: 16, right: 16 }}
                  onClick={(e) => { e.stopPropagation(); setTryOnOpen(true); }}
                >
                  <Chip
                    icon={<Box component="span" sx={{ fontSize: "16px" }}>📷</Box>}
                    label="Try-On"
                    size="small"
                    sx={{
                      bgcolor: "rgba(0, 0, 0, 0.8)",
                      color: "white",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      boxShadow: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.9)" },
                    }}
                  />
                </Box>
              </MainImageContainer>
            </Box>
          </Box>
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
                {product.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Rating value={rating} readOnly precision={0.1} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {rating} ({reviewCount} reviews)
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                {isOnSale ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#D32F2F", letterSpacing: "-0.3px" }}>
                        ${Math.floor(displayPrice)}
                      </Typography>
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: "#D32F2F", ml: "1px" }}>
                        {displayPrice.toFixed(2).slice(-3)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "1rem", color: "#9E9E9E", textDecoration: "line-through" }}>
                      ${originalPrice.toFixed(2)}
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.3px" }}>
                      ${Math.floor(displayPrice)}
                    </Typography>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, ml: "1px" }}>
                      {displayPrice.toFixed(2).slice(-3)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography variant="body1" color="text.secondary" paragraph>
                {product.description || "No description available."}
              </Typography>
            </Box>

           

            {sizeList.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Size
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {sizeList.map((size, index) => (
                    <SizeButton
                      key={index}
                      selected={selectedSize === size}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </SizeButton>
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Quantity
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <QuantitySelector>
                  <QuantityButton onClick={decrementQuantity} disabled={quantity <= 1}>
                    <RemoveIcon fontSize="small" />
                  </QuantityButton>
                  <Typography sx={{ px: 3, fontWeight: 600, minWidth: 40, textAlign: "center" }}>
                    {quantity}
                  </Typography>
                  <QuantityButton onClick={incrementQuantity}>
                    <AddIcon fontSize="small" />
                  </QuantityButton>
                </QuantitySelector>
                <Typography variant="body2" color="text.secondary">
                  Only {Math.min(9, Math.floor(Math.random() * 10) + 1)} left
                </Typography>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={handleAddToCart}
                disabled={!selectedSize}
                variant="contained"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  textTransform: "none",
                  bgcolor: "grey.900",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "grey.800" },
                  ...(isInCart && {
                    bgcolor: "success.main",
                    "&:hover": { bgcolor: "success.dark" },
                  }),
                }}
              >
                {isInCart ? "✓ Added to Cart" : selectedSize ? "Add to Cart" : "Select a Size"}
              </Button>
              <WishlistToggle product={product} />
              <IconButton sx={{ border: "1px solid #e0e0e0" }}>
                <ShareIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={3} sx={{ pt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalShippingIcon fontSize="small" /> <Typography variant="body2">Free Shipping</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityIcon fontSize="small" /> <Typography variant="body2">Secure Payment</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RefreshIcon fontSize="small" /> <Typography variant="body2">Easy Returns</Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">SKU</Typography>
                  <Typography variant="body2" fontWeight={500}>{product.id || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.categories?.[0]?.split(" > ").pop() || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Material</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.attributes?.fabric || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Fit</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {product.attributes?.fit || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {completeTheLook?.recommendations?.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Complete the Look
          </Typography>

          {loadingCompleteTheLook ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {completeTheLook.recommendations.map((rec, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <ProductCard onClick={() => handleCompleteTheLookClick(rec)}>
                    {rec.styleMatch && (
                      <Box sx={{ position: "absolute", top: 8, left: 8 }}>
                        <Chip label="Style Match" size="small" sx={{ bgcolor: "#4CAF50", color: "white", fontWeight: 600, fontSize: "0.7rem" }} />
                      </Box>
                    )}
                    <CardMedia
                      component="img"
                      height="200"
                      image={cleanImageUrl(rec.image) || `https://picsum.photos/seed/${safeSeed(rec.id)}/400/300`}
                      alt={rec.name}
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${safeSeed(rec.id)}/400/300`;
                      }}
                    />
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, mr: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {rec.name}
                        </Typography>
                        <Box sx={{ flexShrink: 0, mt: "-2px" }}>
                          <WishlistToggle product={rec} />
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {rec.productType}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6" fontWeight={700}>
                          ${rec.price?.toFixed(2) || "0.00"}
                        </Typography>
                        {rec.personalizedBoost > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Rating value={5} max={5} readOnly size="small" sx={{ fontSize: "0.9rem" }} />
                            <Typography variant="caption" color="text.secondary">{rec.score?.toFixed(1)}</Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </ProductCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      <VirtualTryOn
        open={tryOnOpen}
        onClose={() => setTryOnOpen(false)}
        productImage={
          allImages[currentImageIndex]?.url
            ? cleanImageUrl(allImages[currentImageIndex].url)
            : ""
        }
        article_id={product.id}
      />
    </Container>
  );
}